import Markdown from 'marked-react';
import { useEffect, useState } from 'react';
import request from '../helpers/request';
import { Box, Card, CardOverflow, Typography, Grid, Divider, Button, CircularProgress, Modal, ModalDialog, ModalClose } from '@mui/joy';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { useDispatch } from 'react-redux';
import { logout, setAgreement } from '../redux/authReducer';

const getAgreementContent = (agreement) => {
    if (!agreement || typeof agreement !== 'object') return '';
    if (typeof agreement.text === 'string') return agreement.text;
    if (typeof agreement.content === 'string') return agreement.content;
    return '';
};

const hasStoredAgreement = (agreementValue) => {
    return ![null, undefined, false, 'false', '', 'null'].includes(agreementValue);
};

const isAgreementLatest = (agreementValue, latestAgreementId) => {
    if (agreementValue === true || agreementValue === 'true') return true;
    if (latestAgreementId == null) return false;

    const parsedAgreementId = Number(agreementValue);
    return Number.isInteger(parsedAgreementId) && parsedAgreementId === latestAgreementId;
};

const Agreement = () => {
    const [userAgreement, setUserAgreement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agreementError, setAgreementError] = useState('');
    const getUserAgreement = async () => {
        setLoading(true);
        setAgreementError('');

        try {
            const response = await request.get('/api/v1/agreement/');
            const agreementContent = getAgreementContent(response.data);
            setUserAgreement({
                ...response.data,
                text: agreementContent,
            });
        }
        catch (error) {
            setUserAgreement(null);
            setAgreementError('Unable to load agreement content. Please try again.');
            console.error(error);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getUserAgreement();
    }, []);

    const { isAuthenticated, user } = useSelector(state => state.auth);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const updateAgreement = async () => {
        if (!userAgreement?.id) {
            toast.error('Agreement content is unavailable');
            return;
        }

        try {
            await request.post('/api/v1/agreement/');

            dispatch(setAgreement({ agreement: true, agreement_declined: false }));
            toast.success('Agreement signed successfully');
            navigate('/app/tasks', { replace: true, state: { fromAgreementAccepted: true } });
        }
        catch (error) {
            toast.error('Failed to sign agreement');
            console.error(error);
        }
    }

    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const declineAgreement = async () => {
        if (!userAgreement?.id) {
            toast.error('Agreement content is unavailable');
            return;
        }

        try {
            await request.patch(`/api/v1/users/${user.id}/`, { signed_at: null, agreement: userAgreement.id });
            dispatch(setAgreement({ agreement: null, agreement_declined: true }));

            toast.error('Agreement declined');
            dispatch(logout());
            navigate('/login', { replace: true });
        }
        catch (error) {
            toast.error('Failed to decline agreement');
            console.error(error);
        }
    }

    return (
        <>
            {loading ? (
                <Grid container justifyContent="center" sx={{ my: 2 }}>
                    <CircularProgress />
                </Grid>
            ) : (
                <>
                    <Modal
                        open={showDeclineModal}
                        onClose={() => { setShowDeclineModal(false) }}
                    >
                        <ModalDialog>
                            <ModalClose />
                            <Typography level="h4" component="h2" sx={{ mb: 1 }}>
                                Are you sure you want to decline?
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                By declining this agreement, your account will be scheduled for disabling. You will still have access to all features until the end of your current billing cycle.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button variant="plain" color="neutral" onClick={() => setShowDeclineModal(false)}>
                                    Cancel
                                </Button>
                                <Button variant="solid" color="danger" onClick={declineAgreement}>
                                    Decline Agreement
                                </Button>
                            </Box>
                        </ModalDialog>
                    </Modal>

                    <Grid container justifyContent="center" sx={{ my: 2 }}>
                        <Grid item xs={6}>
                            <Card>
                                <CardOverflow sx={{ px: 0 }}>
                                    <Typography level='h1' sx={{ textAlign: 'center', py: 2 }}>
                                        {!hasStoredAgreement(user?.agreement) || isAgreementLatest(user?.agreement, userAgreement?.id)
                                            ? 'Terms of Service'
                                            : 'Updated Terms of Service'}
                                    </Typography>
                                    <Divider />
                                    {userAgreement?.text ? (
                                        <Box sx={{ maxHeight: '80vh', px: 3, overflow: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#888', borderRadius: '2px' }, '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#555' } }}>
                                            <Markdown>{userAgreement.text}</Markdown>
                                        </Box>
                                    ) : (
                                        <Box sx={{ px: 3, py: 4 }}>
                                            <Typography level="body-md" color="danger">
                                                {agreementError || 'Agreement content is currently unavailable.'}
                                            </Typography>
                                            <Button sx={{ mt: 2 }} variant="soft" onClick={getUserAgreement}>
                                                Retry
                                            </Button>
                                        </Box>
                                    )}
                                </CardOverflow>
                            </Card>
                        </Grid>
                    </Grid>
                    {isAuthenticated && !!userAgreement?.id && (
                        <Grid container justifyContent="center">
                            <Grid item xs={6}>
                                <Grid container justifyContent="flex-end" spacing={1}>
                                    <Grid item>
                                        <Button onClick={() => setShowDeclineModal(true)} variant="outlined">
                                            Decline
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button onClick={updateAgreement}>Agree</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </>
            )}
        </>
    );
}

export default Agreement;
